import { MINUTES_IN_HOUR } from '@stdlib/constants-time';
import { Spin, message } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  Event,
  EventProps,
  SlotInfo,
  ToolbarProps,
} from 'react-big-calendar';
import { Calendar as BigCalendar } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';

import { inventoryStore } from '../../../../InventoryStore';
import { formatExperienceName } from '../../../../_components/TabViews/tabViewUtils';

import styles from './Calendar.module.scss';
import { ApplyChangesModal } from './_components/ApplyChangesModal/ApplyChangesModal';
import type {
  AvailabilityFormValues,
  AvailabilityInitialValues,
} from './_components/AvailabilityForm/availabilityForm.types';
import {
  AvailabilityPopoverProvider,
  useAvailabilityPopoverGroup,
} from './_components/AvailabilityPopover/availabilityPopoverContext';
import { SeriesHighlightProvider } from './_components/CalendarEvent/seriesHighlightContext';
import { CalendarHeader } from './_components/CalendarHeader/CalendarHeader';
import {
  CalendarToolBar,
  NEW_AVAILABILITY_POPOVER_ID,
} from './_components/CalendarToolBar/CalendarToolBar';
import { EventPopover } from './_components/EventPopover/EventPopover';
import { OverlapWarningModal } from './_components/OverlapWarningModal/OverlapWarningModal';
import { useCalendarScroll } from './hooks/useCalendarScroll';
import { useTimeslotDraftPreview } from './hooks/useTimeslotDraftPreview';
import { useTimeslotEvents } from './hooks/useTimeslotEvents';
import {
  CALENDAR_CONFIG,
  eventPropGetter,
  localizer,
} from './utils/calendarConfig';
import { isDayFullyClosed } from './utils/dayStatus';
import { experienceDurationInMinutes } from './utils/duration';
import {
  getEarliestOpeningStartMinutes,
  isWithinOpeningHours,
} from './utils/openingHours';
import { toCalendarPeriod } from './utils/payload';
import { isRecurrentEvent } from './utils/series';

type CalendarProps = {
  activities: ExtranetHotelExperience[];
};

const WEEK_VIEWS: ['week'] = ['week'];
const CALENDAR_STYLE = { height: '100%' };
const CALENDAR_FORMATS = {
  dayFormat: 'ddd DD/MM',
  timeGutterFormat: 'HH:mm',
};
const DEFAULT_SCROLL_MINUTES = 8 * MINUTES_IN_HOUR;

const CalendarView = ({ activities }: CalendarProps) => {
  const { data: experiencesCategories } = useExperiencesCategories();
  const { activeId, setActiveId } = useAvailabilityPopoverGroup();

  const {
    temporaryEvents,
    preview,
    clear: clearPreview,
  } = useTimeslotDraftPreview();

  const [date, setDate] = useState(() => new Date());
  const [newSlotDraft, setNewSlotDraft] = useState<{
    date: Dayjs;
    startTime: Dayjs;
  } | null>(null);

  const selectedActivityId = useStore(
    inventoryStore,
    (state) => state.timeslotsModal.selectedActivityId
  );

  const selectedActivity = activities.find(
    (activity) => activity.id === selectedActivityId
  );

  const selectedActivityName = selectedActivity
    ? formatExperienceName(
        selectedActivity,
        experiencesCategories?.find(
          (cat) => cat.id === selectedActivity.categoryId
        )?.name
      )
    : '';

  const durationMinutes =
    experienceDurationInMinutes(selectedActivity) ?? undefined;

  const openingHours = selectedActivity?.openingHours;

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToMinutes = useMemo(() => {
    const earliest = getEarliestOpeningStartMinutes(openingHours);

    return earliest !== null
      ? Math.max(0, earliest - MINUTES_IN_HOUR)
      : DEFAULT_SCROLL_MINUTES;
  }, [openingHours]);

  const scrollToTime = useMemo(() => {
    const time = new Date();

    time.setHours(
      Math.floor(scrollToMinutes / MINUTES_IN_HOUR),
      scrollToMinutes % MINUTES_IN_HOUR,
      0,
      0
    );

    return time;
  }, [scrollToMinutes]);

  useCalendarScroll(containerRef, scrollToMinutes, date);

  const isDayClosed = useCallback(
    (day: Dayjs) => isDayFullyClosed(selectedActivity, day.toDate()),
    [selectedActivity]
  );

  const isSlotWithinOpeningHours = useCallback(
    (start: Dayjs) => isWithinOpeningHours(openingHours, start.toDate()),
    [openingHours]
  );

  const { hotelId } = useContext(HotelContext);
  const calendarId = selectedActivity?.calendarId;
  const period = useMemo(() => toCalendarPeriod(date), [date]);

  const {
    events,
    isLoading,
    isValidatingOverlap,
    pendingOverlap,
    pendingEdit,
    handleCreateEvent,
    handleConfirmOverlap,
    handleSaveEvent,
    handleDeleteEvent,
    handleConfirmEdit,
    cancelOverlap,
    cancelEdit,
  } = useTimeslotEvents({
    clearPreview,
    isDayClosed,
    hotelId,
    calendarId,
    period,
  });

  const slotPropGetter = useCallback(
    (slotDate: Date) => {
      if (isDayFullyClosed(selectedActivity, slotDate)) {
        return { className: styles.closedDay };
      }

      if (!isWithinOpeningHours(openingHours, slotDate)) {
        return { className: styles.outsideOpeningHours };
      }

      return {};
    },
    [openingHours, selectedActivity]
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      if (slotInfo.action !== 'click') {
        return;
      }

      const clicked = dayjs(slotInfo.start);

      if (clicked.isBefore(dayjs())) {
        message.warning('Cannot create a slot in the past');

        return;
      }

      setNewSlotDraft({ date: clicked, startTime: clicked });
      setActiveId(NEW_AVAILABILITY_POPOVER_ID);
    },
    [setActiveId]
  );

  useEffect(() => {
    if (activeId !== NEW_AVAILABILITY_POPOVER_ID) {
      setNewSlotDraft(null);
    }
  }, [activeId]);

  const createInitialValues = useMemo<AvailabilityInitialValues | undefined>(
    () =>
      newSlotDraft
        ? {
            date: newSlotDraft.date,
            startTime: newSlotDraft.startTime,
            capacity: 1,
          }
        : undefined,
    [newSlotDraft]
  );

  const previewCreate = useCallback(
    (values: AvailabilityFormValues | null) =>
      preview(values, undefined, isDayClosed),
    [preview, isDayClosed]
  );

  const previewEdit = useCallback(
    (event: Event, values: AvailabilityFormValues | null) =>
      isRecurrentEvent(event)
        ? preview(null)
        : preview(values, dayjs(event.start), isDayClosed),
    [preview, isDayClosed]
  );

  const components = useMemo(
    () => ({
      toolbar: (props: ToolbarProps) => (
        <CalendarToolBar
          {...props}
          onCreate={handleCreateEvent}
          onCreateChange={previewCreate}
          createInitialValues={createInitialValues}
          durationMinutes={durationMinutes}
          selectActivityName={selectedActivityName}
          isWithinOpeningHours={isSlotWithinOpeningHours}
          isDayClosed={isDayClosed}
        />
      ),
      header: (props: { date: Date; label: string }) => (
        <CalendarHeader
          label={props.label}
          closed={isDayFullyClosed(selectedActivity, props.date)}
        />
      ),
      event: (props: EventProps) => (
        <EventPopover
          {...props}
          activityName={selectedActivityName}
          durationMinutes={durationMinutes}
          onPreview={previewEdit}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          isWithinOpeningHours={isSlotWithinOpeningHours}
          isDayClosed={isDayClosed}
        />
      ),
    }),
    [
      handleCreateEvent,
      previewCreate,
      createInitialValues,
      durationMinutes,
      selectedActivity,
      selectedActivityName,
      previewEdit,
      handleSaveEvent,
      handleDeleteEvent,
      isSlotWithinOpeningHours,
      isDayClosed,
    ]
  );

  const displayedEvents = useMemo(
    () => [...events, ...temporaryEvents],
    [events, temporaryEvents]
  );

  return (
    <>
      <div className={styles.customTimeSlotCalendar} ref={containerRef}>
        {(isLoading || isValidatingOverlap) && (
          <div className={styles.spinnerOverlay}>
            <Spin />
          </div>
        )}
        <BigCalendar
          localizer={localizer}
          events={displayedEvents}
          date={date}
          onNavigate={setDate}
          startAccessor="start"
          endAccessor="end"
          style={CALENDAR_STYLE}
          defaultView="week"
          views={WEEK_VIEWS}
          selectable
          onSelectSlot={handleSelectSlot}
          popup
          showMultiDayTimes
          scrollToTime={scrollToTime}
          step={CALENDAR_CONFIG.STEP}
          timeslots={CALENDAR_CONFIG.TIMESLOTS}
          dayLayoutAlgorithm="no-overlap"
          components={components}
          eventPropGetter={eventPropGetter}
          slotPropGetter={slotPropGetter}
          formats={CALENDAR_FORMATS}
        />
      </div>
      <OverlapWarningModal
        isOpen={pendingOverlap !== null}
        onCancel={cancelOverlap}
        onConfirm={handleConfirmOverlap}
      />
      <ApplyChangesModal
        isOpen={pendingEdit !== null}
        isLoading={isValidatingOverlap}
        mode={pendingEdit?.kind}
        scopes={pendingEdit?.scopes}
        onCancel={cancelEdit}
        onConfirm={handleConfirmEdit}
      />
    </>
  );
};

export const Calendar = (props: CalendarProps) => (
  <AvailabilityPopoverProvider>
    <SeriesHighlightProvider>
      <CalendarView {...props} />
    </SeriesHighlightProvider>
  </AvailabilityPopoverProvider>
);
